import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Modal from 'components/commercetools-ui/atoms/modal';
import Typography from 'components/commercetools-ui/atoms/typography';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import useLocale from 'helpers/hooks/useLocale';
import { StringHelpers } from 'helpers/stringHelpers';
import CustomfitOptions from './customfit-options';
import { ProductDetailsProps } from '..';
import { discardRepeatedValues } from '../helpers/discardRepeatedValues';
import VariantOptions from './variantOptions';
import { Money, Variant } from 'shared/types/product';
import { CartDiscount, promotionCategory, SelectedAttributes } from '../types';
import CustomfitVariant from './customfit-variant';
import { useCookies } from 'react-cookie';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import { Price } from 'shared/types/product/Variant';
import sale from 'public/locales/en/product.json';
import Link from 'components/commercetools-ui/atoms/link';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { TableSet } from 'shared/types/product/Product';
import { useSearchParams } from 'next/navigation';
import CustomFitNew from './customfit-new';

const typeMapping: Record<string, string> = {
  waistsize: 'waist',
  leglength: 'leg',
};

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart'>;
const ProductInformation: FC<ProductInformationProps> = ({
  product,
  variant,
  onChangeVariant,
  inModalVersion,
  handleScroll,
  setIsCartDisable,
  setHoveredImage,
  setCustomFitPrice,
  customFitPrice,
  setQuantity,
  quantity,
  setPayloadAttributes,
  setSelectedSKUs,
  payloadAttributes,
  isCartDisable,
  selectedSKUs,
  breadCrumb,
}) => {
  const router = useRouter();
  const { ctLocale: locale } = useLocale();
  const [isSizeGuideModalOpen, setIsSizeGuideModalOpen] = useState(false);
  const sizeOverlayContentsRef = useRef<HTMLIFrameElement | null>(null);
  const variantValue = useSearchParams();
  const paramValue = variantValue.get('variant');
  const [variantSelected, setVariantSelected] = useState(false);
  // const [quantity, setQuantity] = useState<number>(1);
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const [selectedAttributes, setSelectedAttributes] = useState<SelectedAttributes>({
    colour: variant?.attributes?.colour || '',
    size: '',
    shoesize: '',
    waist: '',
    leg: '',
    hand: '',
  });
  const [price, setPrice] = useState<number>(0);
  const [discountedPrices, setDiscountedPrices] = useState<any>(null);
  const [fractionDigits, setFractionDigits] = useState<number>(2);
  const [currencyCode, setCurrencyCode] = useState<string>('GBP');
  // const [customFitPrice, setCustomFitPrice] = useState<number | null>(null);
  const originalOrder = ['colour', 'size', 'shoesize', 'waistsize', 'leglength', 'hand'];
  const path = usePathname();
  const tableSet = product?.sizeGuide?.tableSet as TableSet[];
  const sizeData = [
    { uk: '4', us: '6', eu: '37', mm: '240' },
    { uk: '4.5', us: '6.5', eu: '37.5', mm: '245' },
    { uk: '5', us: '7', eu: '38', mm: '249' },
    { uk: '5.5', us: '7.5', eu: '38.5', mm: '253' },
    { uk: '6', us: '8', eu: '39', mm: '257' },
    { uk: '6.5', us: '8.5', eu: '40.5', mm: '262' },
    { uk: '7', us: '9', eu: '41', mm: '266' },
  ];
  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string | null = localeSelection?.currency || null;
  const promotionCategory = product?.categories as promotionCategory[] | undefined;

  const saleCategory = promotionCategory?.some((i: promotionCategory) => i?.slug == sale.sale.slug);
  const agExclusive = promotionCategory?.some((i: promotionCategory) => i?.slug == sale['AG-Exclusive'].slug);
  const newArrivals = promotionCategory?.find((i: promotionCategory) => i?.name === 'New Arrivals');
  const discountCategory = promotionCategory?.find(
    (category: promotionCategory) => category.name && /\d+%/.test(category.name),
  );
  const isIronsCategory =
    promotionCategory?.some((i: promotionCategory) => i?.name?.toLowerCase().includes('irons')) &&
    !!product?.customFitDetails;

  const discountPercent = discountCategory?.name.match(/(\d+)%/)?.[1];

  const discountPromo = discountPercent ? `+Extra ${discountPercent}% Off At Checkout` : '';

  const discountUrl = discountCategory?.slug ?? '';
  // As of now Commented : currently we are getting price from standalonePrice in attributes
  // const price = variant?.prices?.filter(
  //   (i: Price) => i?.currencyCode === cookies?.locale_selection?.currency,
  // ) as Price[];
  // const standalonePrice = variant?.attributes?.standalonePrice ? JSON.parse(variant.attributes.standalonePrice) : {};

  // // Get price object based on locale selection
  // const priceObj = standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.value;

  const BrandLogo = `https://${product?.brandLogo?.BrandImage?.endpoint}.a.bigcontent.io/v1/static/${product?.brandLogo?.BrandImage?.name}`;

  const currentSku = product?.slug;
  // As of now Commented
  // const discountPercentage =
  // variant.discountedPrice &&
  // (((price?.[0]?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
  //   (price?.[0]?.centAmount as number)) *
  //   100;
  // const discountPercentage =
  //   variant.discountedPrice &&
  //   variant.discountedPrice &&
  //   ((price - (variant.discountedPrice.centAmount as number)) / price) * 100;
  const discountPercentage = useMemo(() => {
    if (!discountedPrices || !discountedPrices?.mrrp?.centAmount || !price) {
      return null; // Return null if required data is missing
    }

    const mrrp = discountedPrices?.mrrp?.centAmount;
    const wasPrice = discountedPrices?.wasPrice?.centAmount;

    if (mrrp <= 0 || price <= 0 || mrrp === price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrp - price) / mrrp) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [discountedPrices]);

  // {*The below code for addToWishList is movied to product-details\index.tsx file so comminthing the code here*}
  // const productToWishlistLineItem = useMemo<LineItem>(() => {
  //     return {
  //         lineItemId: product?.productId ?? '',
  //         productId: product?.productId,
  //         name: product?.name,
  //         count: 1,
  //         variant: variant,
  //         addedAt: new Date(),
  //         _url: product?._url,
  //     };
  // }, [product, variant]);

  const handleQuantityChange = (e: React.FormEvent) => {
    setQuantity(+(e.target as HTMLSelectElement).value);
  };
  const titleClassName = useClassNames(['break-normal mb-12', { 'md:text-24': !inModalVersion }]);
  const priceClassName = useClassNames([
    'block leading-loose',
    { 'line-through text-[#4B4B4B] font-normal leading-4 font-body text-14': !!discountPercentage },
    { 'text-14 font-bold leading-6 font-body': !inModalVersion },
    { 'text-22': !!inModalVersion && !discountPercentage },
    { 'text-14': !!inModalVersion && !!discountPercentage },
  ]);
  const discountedPriceClassName = useClassNames([
    'block font-bold font-body leading-6 text-[#EB0E0E] text-20',
    { 'text-20': !inModalVersion },
  ]);
  const showSizeGuide = () => {
    // const iframe = document.createElement('iframe');
    // sizeOverlayContentsRef.current = iframe;
    // iframe.style.cssText = `height: 100%; width: 100%`;
    // iframe.src = `https://www.americangolf.co.uk/on/demandware.store/Sites-AmericanGolf-GB-Site/en_GB/Product-SizeGuide?pid=${variant.sku}`;
    setIsSizeGuideModalOpen(true);
  };
  const { optionsOrder = [] } = variant?.attributes || {};
  const cartDiscount = (variant?.attributes?.cartDiscount &&
    JSON.parse(variant?.attributes?.cartDiscount as string)) as CartDiscount;
  const parentCategoryLink = product?.breadCrumb;

  const sortedOptionsOrder = optionsOrder
    ?.filter((attribute) => attribute !== 'model' && attribute !== 'width') // Exclude 'model' && 'width' attributes
    ?.sort((a, b) => {
      const indexA = originalOrder?.indexOf(a);
      const indexB = originalOrder?.indexOf(b);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  const customFitVariant = product?.variants.find((variant) => variant?.attributes?.isCustomFit === true);
  const isGPSCategory = !!product?.categories?.some((category) => category.name === 'GPS');
  const isGolfClubProducts = product?.categories?.some((category: any) =>
    /golf-clubs|clubs|CLUBS|GOLF-CLUBS/.test(category?.slug),
  );

  const changeRoute = (updatedSku: string) => {
    if (updatedSku && updatedSku !== '') {
      const newPath = `${window.location.pathname}?variant=${updatedSku}`;
      return router.push(`${newPath}`);
    }
    return router.push(window.location.pathname.split('?')[0]);
  };

  const VariationsSection = useCallback(() => {
    return sortedOptionsOrder?.length >= 1 ? (
      <div className="relative mb-16 flex flex-col gap-16 py-5">
        {sortedOptionsOrder?.map((type, index) => {
          const attributeType = typeMapping[type] || type;
          const variants: Variant[] = discardRepeatedValues(product?.variants, attributeType) ?? [];
          return (
            <VariantOptions
              key={index}
              type={type}
              showSizeGuide={showSizeGuide}
              variants={variants}
              product={product}
              selectedAttributes={selectedAttributes}
              setSelectedAttributes={setSelectedAttributes}
              optionsOrder={sortedOptionsOrder}
              changeRoute={changeRoute}
              onChangeVariant={onChangeVariant}
              isGPSCategory={isGPSCategory}
              setHoveredImage={setHoveredImage}
              setVariantSelected={setVariantSelected}
              variantSelected={variantSelected}
              tableSet={tableSet}
              setIsCartDisable={setIsCartDisable}
            />
          );
        })}
      </div>
    ) : (
      <></>
    );
  }, [currentSku, selectedAttributes]);

  // useEffect(() => {
  //   if ((sortedOptionsOrder as any)?.length > 0 && product?.variants?.length > 0) {
  //     const updatedAttributes: SelectedAttributes = { ...selectedAttributes };

  //     (sortedOptionsOrder as any).forEach((attribute: any) => {
  //       const uniqueValues = [
  //         ...new Set(product.variants.map((variant: Variant) => variant.attributes?.[attribute]).filter(Boolean)),
  //       ];

  //       // Only update if the attribute is not already set by the user
  //       if (!(selectedAttributes as any)[attribute] && uniqueValues.length === 1) {
  //         (updatedAttributes as any)[attribute] = uniqueValues[0]; // Auto-select if only one unique value exists
  //       }
  //     });

  //     // Update selectedAttributes only if there are changes
  //     if (JSON.stringify(updatedAttributes) !== JSON.stringify(selectedAttributes)) {
  //       setSelectedAttributes(updatedAttributes);
  //     }
  //   }
  // }, [sortedOptionsOrder, product?.variants, selectedAttributes]);

  useEffect(() => {
    if (product?.variants) {
      const skus = product.variants
        .filter((v) => v.sku)
        .map((v) => StringHelpers.removeWhiteSpace(v.sku))
        .join(',');
      if (skus) {
        setTrustPilotSkuId(skus);
        localStorage.setItem('currentlySelectedVariants', skus);
      }
    }
  }, [product]);

  // useEffect(() => {
  //   const selectedCurrency = cookies?.locale_selection?.currency;
  //   if (
  //     selectedCurrency &&
  //     product?.customFitDetails?.CF_Price &&
  //     selectedCurrency in product.customFitDetails.CF_Price
  //   ) {
  //     setCustomFitPrice(
  //       product.customFitDetails.CF_Price[selectedCurrency as keyof typeof product.customFitDetails.CF_Price],
  //     );
  //   } else {
  //     setCustomFitPrice(null);
  //   }
  // }, []);

  useEffect(() => {
    if (variant?.attributes?.standalonePrice) {
      try {
        const standalonePrice = JSON.parse(variant.attributes.standalonePrice);
        const selectedPrice =
          standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.value;
        const discountedPrices =
          standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.custom || null;
        if (selectedPrice) {
          setPrice(selectedPrice.centAmount);
          setFractionDigits(selectedPrice.fractionDigits ?? 2);
          setCurrencyCode(selectedPrice.currencyCode);
        }
        if (discountedPrices) {
          setDiscountedPrices(discountedPrices);
        } else {
          setDiscountedPrices(null);
        }
      } catch (error) {
        console.error('Error parsing standalonePrice:', error);
      }
    }
  }, [variant, cookies?.locale_selection?.currency]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const hasAllNonEmptyValues =
      payloadAttributes.length > 0 &&
      payloadAttributes.every((attr: any) => {
        const value = attr.value;
        return Array.isArray(value) ? value.length > 0 : value !== '';
      });

    if (hasAllNonEmptyValues) {
      const dataLayerProductUpdatePush = new DataLayerHelpers(path, locale, variant);
      dataLayerProductUpdatePush.pushProductViewUpdateToDataLayer(
        product,
        variant,
        cookies?.locale_selection?.currency,
        selectedAttributes,
        payloadAttributes,
        isGolfClubProducts,
        customFitVariant,
      );
    }
  }, [JSON.stringify(payloadAttributes.map((attr: any) => attr.value))]);
  const [previousVariant, setPreviousVariant] = useState<string | null>(null);

  useEffect(() => {
    if (variantSelected && !isCartDisable && (previousVariant as any) !== variantSelected) {
      setPreviousVariant(variantSelected as any);

      const hasNonEmptySelectedAttributes = Object.values(selectedAttributes || {}).some(
        (value) => value.trim() !== '',
      );

      if (hasNonEmptySelectedAttributes) {
        const dataLayerProductUpdatePush = new DataLayerHelpers(path, locale, variant);
        dataLayerProductUpdatePush.pushProductViewUpdateToDataLayer(
          product,
          variant,
          cookies?.locale_selection?.currency,
          selectedAttributes,
          payloadAttributes,
          isGolfClubProducts,
          customFitVariant,
        );
      }
    }
  }, [
    variantSelected,
    isCartDisable,
    selectedAttributes,
    path,
    locale,
    variant,
    cookies,
    payloadAttributes,
    isGolfClubProducts,
    customFitVariant,
    previousVariant,
  ]);

  return (
    <div>
      <div className="mb-5 hidden sm:block">
        <div className="flex flex-col gap-y-4">
          {product?.brandLogo && (
            <div className="w-[100%] max-w-[150px]">
              <img src={BrandLogo} className="w-[100%]" />
            </div>
          )}
          <Typography as="h1" className={`${titleClassName} text-start font-semibold leading-8`}>
            {product?.name}
          </Typography>
        </div>
        {/* As of now Commentted - Need to confirm */}
        {/* <div className="mb-10 py-7">
          <TrustBox id={trustPilotSkuId} showVertical={false} />
        </div> */}

        <div className="mb-20 flex gap-x-12">
          {saleCategory && (
            <a href={sale.sale.url}>
              <div className="flex h-[26px] min-w-[56px] cursor-pointer items-center justify-center rounded-md bg-[#E30616] p-7 text-12 font-medium leading-3 text-white">
                {sale?.sale?.name}
              </div>
            </a>
          )}
          {agExclusive && (
            <a href={sale['AG-Exclusive'].url}>
              <div className="flex h-[26px] min-w-[100px] cursor-pointer items-center justify-center rounded-md bg-black p-7 text-12 font-medium leading-3 text-white">
                {sale['AG-Exclusive']?.name}
              </div>
            </a>
          )}
          {newArrivals && (
            <a href={`/${newArrivals?.slug}`}>
              <div className="flex h-[26px] min-w-[59px] cursor-pointer items-center justify-center rounded-md bg-[#127E57] p-7 font-body text-12 font-medium capitalize leading-3 text-white">
                {newArrivals?.slug}
              </div>
            </a>
          )}
        </div>
        {discountPromo && (
          <a href={`/${discountUrl}`}>
            <div className="inline-flex w-full cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
              {discountPromo}
            </div>
          </a>
        )}

        {cartDiscount?.description?.['en-GB'] && (
          <Link
            link={`${breadCrumb?.[breadCrumb?.length - 1]?.href}shop-by/offer/${
              cartDiscount?.description?.['en-GB']
                .replace(/\s+/g, '-') // Replace spaces with dashes
                .replace('€', '€') // Ensure the Euro sign remains as is
            }`}
          >
            <div
              className="inline-flex cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium capitalize leading-7"
              // onClick={handleScroll}
            >
              {cartDiscount?.description?.['en-GB']}
            </div>
          </Link>
        )}
      </div>
      <div>
        {customFitVariant || isGolfClubProducts ? (
          <div className="mt-[1rem]">
            {product?.customFitDetails ? (
              <CustomFitNew
                product={product}
                // variant={customFitVariant}
                setIsCartDisable={setIsCartDisable}
                setCustomFitPrice={setCustomFitPrice}
                customFitPrice={customFitPrice}
                scrollable={true}
                setPayloadAttributes={setPayloadAttributes}
                setSelectedSKUs={setSelectedSKUs}
                setQuantity={setQuantity}
                currency={currency}
                selectedSKUs={selectedSKUs}
              />
            ) : (
              // <CustomfitVariant
              //   product={product}
              //   // variant={customFitVariant}
              //   setIsCartDisable={setIsCartDisable}
              //   setCustomFitPrice={setCustomFitPrice}
              //   customFitPrice={customFitPrice}
              //   scrollable={true}
              //   setPayloadAttributes={setPayloadAttributes}
              //   setSelectedSKUs={setSelectedSKUs}
              //   setQuantity={setQuantity}
              //   currency={currency}
              // />
              <CustomfitOptions
                product={product}
                variant={variant}
                setIsCartDisable={setIsCartDisable}
                onChangeVariant={onChangeVariant}
                changeRoute={changeRoute}
              />
            )}
          </div>
        ) : (
          <VariationsSection />
        )}
      </div>

      <div className="pt-8">
        {!isIronsCategory && ( // Only render the quantity selector if isIronsCategory is false
          <div className="flex flex-col items-start gap-4">
            <Typography className="mb-4 text-start text-14 font-normal leading-loose">{'Quantity Selector'}</Typography>
            {/* <Dropdown
      className="h-full rounded-sm"
      value={quantity.toString()}
      items={Array(10)
        .fill(0)
        .map((_, index) => {
          const value = `${index + 1}`;
          return {
            label: value,
            value,
          };
        })}
      onChange={handleQuantityChange}
    /> */}
            <div className="flex h-[40px] w-[132px] items-center justify-around gap-2 rounded-md border border-[#686868] xl:w-[42%]">
              <button
                className={`flex h-8 w-8 items-center justify-center rounded-full text-lg font-normal ${
                  quantity === 1 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-200'
                }`}
                onClick={() => setQuantity((prev: number) => Math.max(1, prev - 1))} // Decrease quantity but not below 1
                disabled={quantity === 1} // Disable when quantity is 1
              >
                -
              </button>
              <span className="flex h-8 w-8 items-center justify-center text-lg font-normal">{quantity}</span>
              <button
                className={`flex h-8 w-8 items-center justify-center rounded-full text-lg font-normal ${
                  quantity === 10 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-200'
                }`}
                onClick={() => setQuantity((prev: any) => Math.min(prev + 1, 10))} // Increase quantity but not above 10
                disabled={quantity === 10} // Disable when quantity is 10
              >
                +
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center pt-12">
        {discountPercentage ? (
          <div className="flex flex-row">
            <div className="flex flex-col items-start gap-y-4">
              <p className={priceClassName}>
                MRRP {CurrencyHelpers.formatForCurrency(discountedPrices?.mrrp ?? 0, locale)}
              </p>
              <p className="block font-body text-20 font-bold leading-6 text-[#EB0E0E]">
                {/* Need to Fix */}
                {/* Now  {CurrencyHelpers.formatForCurrency(variant.discountedPrice as number, locale)} */}
                {/* Now {currency === 'GBP' ? '£' : '€'} {((variant?.discountedPrice as any)?.centAmount / 100).toFixed(2)} */}
                Now {CurrencyHelpers.formatForCurrency(price, locale, currencyCode, fractionDigits)}
              </p>
            </div>
            <span className="ml-20 mt-10 flex h-[26px] min-w-[78px] justify-center self-end rounded-md border border-black p-5 font-body text-14 font-medium leading-[16.4px] text-black">
              Save {Math.round(discountPercentage)}%
            </span>
          </div>
        ) : customFitVariant && customFitPrice ? (
          <div className="flex items-center">
            <p className="block font-body text-20 font-bold leading-6">
              {currency === 'GBP' ? '£' : '€'}
              {customFitPrice?.toFixed(2)}
            </p>
          </div>
        ) : (
          <div className="flex items-center">
            <Typography className={`${priceClassName} font-body text-20 font-bold leading-6`}>
              {/* As of now Commentted : we are getting from attributes.standalonePrice */}
              {/* {CurrencyHelpers.formatForCurrency(price?.[0] as number, locale)} */}
              {CurrencyHelpers.formatForCurrency(price, locale, currencyCode, fractionDigits)}
            </Typography>
          </div>
        )}
      </div>

      <Modal
        isOpen={isSizeGuideModalOpen}
        onRequestClose={() => {
          setIsSizeGuideModalOpen(false);
          sizeOverlayContentsRef.current = null;
        }}
      >
        <div className="flex flex-col">
          <div className="mb-20 flex items-end justify-end" onClick={() => setIsSizeGuideModalOpen(false)}>
            <button className="mt-[-1px] text-right font-body text-14 font-medium leading-4">{sale.closeButton}</button>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="13"
              height="13"
              viewBox="0 0 48 48"
              className="ml-2 cursor-pointer"
            >
              <path fill="#F44336" d="M21.5 4.5H26.501V43.5H21.5z" transform="rotate(45.001 24 24)"></path>
              <path fill="#F44336" d="M21.5 4.5H26.5V43.501H21.5z" transform="rotate(135.008 24 24)"></path>
            </svg>
          </div>
          <div className="mx-10 flex w-full flex-col items-center">
            {product?.brandLogo && (
              <div className="mb-10 w-[100%] max-w-[150px]">
                <img src={BrandLogo} className="w-[100%]" />
              </div>
            )}
            <h2 className="mb-25 font-body text-34 font-bold capitalize leading-[34px]">
              {product?.variants?.[0]?.attributes?.brand} - {'Size Guide'}
            </h2>
            <p className="mb-25 text-sm text-gray-500">{'Note: Information supplied by manufacturer'}</p>

            {/* Check if there are multiple tableSets */}
            {tableSet?.length > 1 ? (
              // Multiple TableSets: Render Accordion for each
              tableSet.map((table: TableSet, index) => {
                const combinedSizeData = `${table.sizeData || ''} ${table.extraSizeData || ''}`.trim();

                return (
                  <AccordionBtn
                    key={index}
                    className="col-span-2 border-b border-neutral-400 md:mt-0"
                    closedSectionTitle={table?.heading || `Size Guide ${index + 1}`}
                    buttonClassName="py-21 font-body text-16 leading-tight text-primary-black pl-0 md:pr-12"
                    panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
                  >
                    <div className="mb-25 w-full overflow-x-auto">
                      <style>
                        {`
                .styled-size-table th, .styled-size-table td {
                  text-align: center;
                  padding: 10px;
                  border-bottom: 1px solid #ddd;
                }
              `}
                      </style>
                      {combinedSizeData ? (
                        <div
                          className="styled-size-table w-[100%]"
                          dangerouslySetInnerHTML={{
                            __html: combinedSizeData,
                          }}
                        />
                      ) : (
                        <p className="text-sm text-gray-500">
                          No size data available.{' '}
                          <a href="/help" className="text-blue-500 underline">
                            Click here to visit our help section.
                          </a>
                        </p>
                      )}
                    </div>
                  </AccordionBtn>
                );
              })
            ) : (
              // Single TableSet: Render without Accordion
              <div className="mb-25 flex w-full flex-col items-center overflow-x-auto">
                <style>
                  {`
          .styled-size-table th, .styled-size-table td {
            text-align: center;
            padding: 10px;
            border-bottom: 1px solid #ddd;
          }
        `}
                </style>
                <h2 className="mb-25 font-body text-22 font-normal leading-[22px]">
                  {product?.sizeGuide?.tableSet?.[0]?.heading}
                </h2>
                {product?.sizeGuide?.tableSet?.[0]?.sizeData || product?.sizeGuide?.tableSet?.[0]?.extraSizeData ? (
                  <div
                    className="styled-size-table w-[100%]"
                    dangerouslySetInnerHTML={{
                      __html: `${product?.sizeGuide?.tableSet?.[0]?.sizeData || ''} ${
                        product?.sizeGuide?.tableSet?.[0]?.extraSizeData || ''
                      }`.trim(),
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500">
                    No size data available.{' '}
                    <Link link={'https://help.americangolf.co.uk/hc/en-gb'} className="text-blue-500 underline">
                      Click here to visit our help section.
                    </Link>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="text-16 font-normal leading-[19.2px]">{'Note: Information supplied by manufacturer'}</p>
      </Modal>
    </div>
  );
};
export default ProductInformation;
